/**
 * ManifestConnectionForm — shared UI for the manifest-driven form body used
 * by both `EditConnectionDialog` (managed / ARM connections) and
 * `EditServiceProviderConnectionDialog` (local service-provider connections).
 *
 * Renders:
 *   - Display name field
 *   - Divider
 *   - "Authentication" section with `DynamicConnectionForm` (the manifest
 *     drives the field set; the parent owns the values)
 *   - Optional `oauthSlot` at the bottom of the auth section (used by the
 *     managed dialog to render its "Sign in" re-auth affordance)
 *
 * State management lives in `useManifestFormState`. This component is pure
 * presentation — the parent owns the form values and change handlers.
 */

import type { ReactNode } from 'react';
import {
  Divider,
  Field,
  Input,
  makeStyles,
  Spinner,
  Text,
  tokens,
} from '@coreai/fluentui-react';
import { DynamicConnectionForm } from '@/components/WorkflowDesigner/NodeEditor/connections/forms/DynamicConnectionForm';
import type { ConnectionParameterSet, FormValues } from '@/types/connectionParameters';

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
  },
  sectionHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  divider: {
    marginTop: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
  },
  loadingText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  emptyText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
});

export interface ManifestConnectionFormProps {
  /** Current display name (controlled). */
  displayName: string;
  onDisplayNameChange: (value: string) => void;
  /** Placeholder for the display-name input. */
  displayNamePlaceholder?: string;
  /** Hint text below the display-name label. */
  displayNameHint?: string;
  /** Optional test id for the display-name input. */
  displayNameTestId?: string;

  /** All parameter sets from the effective manifest. */
  parameterSets: ConnectionParameterSet[];
  /** Currently selected parameter-set name. */
  selectedParameterSet: string;
  /** Called when the user picks a different auth type. */
  onParameterSetChange: (name: string) => void;

  /** Current parameter values (controlled). */
  values: FormValues;
  /** Called when any field in the dynamic form changes. */
  onValuesChange: (values: FormValues) => void;

  /**
   * True when the manifest has multiple real parameter sets (multi-auth).
   * Passed through to `DynamicConnectionForm.showAuthTypeSelector`.
   */
  showAuthTypeSelector: boolean;

  /** True when everything should be disabled (e.g. during a save). */
  disabled?: boolean;
  /** True while the manifest/metadata is still loading. */
  isLoading?: boolean;

  /**
   * Override for the "Authentication" section title. Defaults to
   * "Authentication".
   */
  authSectionTitle?: string;
  /**
   * Override for the hint under the Authentication title. Defaults to a
   * generic "Update authentication parameters..." message that mentions
   * multi-auth when relevant.
   */
  authSectionHint?: string;

  /**
   * Optional content to render at the end of the Authentication section.
   * The managed dialog uses this for its "Sign in" OAuth re-auth button.
   */
  oauthSlot?: ReactNode;
}

const defaultAuthSectionHint = (isMultiAuth: boolean): string =>
  isMultiAuth
    ? 'Update authentication type and parameters. Secrets will need to be re-entered.'
    : 'Update authentication parameters. Secrets will need to be re-entered.';

/**
 * Presentation-only body for a manifest-driven connection edit form.
 * State lives in `useManifestFormState` (or an equivalent parent-owned bag).
 */
export const ManifestConnectionForm = ({
  displayName,
  onDisplayNameChange,
  displayNamePlaceholder,
  displayNameHint = 'A friendly name for this connection',
  displayNameTestId,
  parameterSets,
  selectedParameterSet,
  onParameterSetChange,
  values,
  onValuesChange,
  showAuthTypeSelector,
  disabled = false,
  isLoading = false,
  authSectionTitle = 'Authentication',
  authSectionHint,
  oauthSlot,
}: ManifestConnectionFormProps) => {
  const styles = useStyles();

  const hint = authSectionHint ?? defaultAuthSectionHint(showAuthTypeSelector);
  const currentParameterSet = parameterSets.find((s) => s.name === selectedParameterSet)
    ?? parameterSets[0];

  return (
    <>
      <div className={styles.section}>
        <Field label="Display name" hint={displayNameHint}>
          <Input
            value={displayName}
            onChange={(_, data) => onDisplayNameChange(data.value)}
            placeholder={displayNamePlaceholder ?? 'Enter display name'}
            disabled={disabled}
            {...(displayNameTestId ? { 'data-testid': displayNameTestId } : {})}
          />
        </Field>
      </div>

      <Divider className={styles.divider} />

      <div className={styles.section}>
        <Text className={styles.sectionTitle}>{authSectionTitle}</Text>
        <Text className={styles.sectionHint}>{hint}</Text>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="tiny" />
            <Text className={styles.loadingText}>Loading authentication details...</Text>
          </div>
        ) : parameterSets.length > 0 && currentParameterSet ? (
          <>
            {/*
              DynamicConnectionForm: form STRUCTURE from the connector
              manifest, VALUES can be edited to update the connection.
              We hide DynamicConnectionForm's built-in displayName field
              because this component owns it above.
            */}
            <DynamicConnectionForm
              parameterSets={parameterSets}
              selectedParameterSet={selectedParameterSet}
              onParameterSetChange={onParameterSetChange}
              values={values}
              onChange={onValuesChange}
              showAuthTypeSelector={showAuthTypeSelector}
              hideDisplayName={true}
            />
            {oauthSlot}
          </>
        ) : (
          <Text className={styles.emptyText}>
            No authentication details available for this connection
          </Text>
        )}
      </div>
    </>
  );
};

ManifestConnectionForm.displayName = 'ManifestConnectionForm';
